/**
 * Two-Factor Authentication Service
 * Supports SMS (via mNotify) and Authenticator App (TOTP)
 */

import { supabase } from './supabase'
// Note: speakeasy and QRCode are Node.js libraries - TOTP features disabled in browser
// For authenticator app support, implement via Supabase Auth MFA or use a backend API

/**
 * Hash a string using Web Crypto API (browser-compatible)
 */
async function hashString(text: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(text)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
}

const MNOTIFY_API_KEY = import.meta.env.VITE_MNOTIFY_API_KEY || ''
const MNOTIFY_SENDER_ID = import.meta.env.VITE_MNOTIFY_SENDER_ID || 'FaithYoke'

if (!MNOTIFY_API_KEY) {
  console.warn('MNotify API key not configured - SMS 2FA will not work')
}

/**
 * Generate a 6-digit OTP code
 */
export function generate2FAOTP(): string {
  return Math.floor(100000 + Math.random() * 900000).toString()
}

/**
 * Send 2FA code via SMS using mNotify
 */
export async function send2FASMS(
  userId: string,
  phone: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const otpCode = generate2FAOTP()
    const expiresAt = new Date(Date.now() + 5 * 60 * 1000) // 5 minutes

    // Store OTP in database
    await supabase.from('two_factor_codes').insert({
      user_id: userId,
      code: await hashString(otpCode),
      expires_at: expiresAt.toISOString(),
      method: 'sms'
    })

    // Check if it's a Ghana number
    const isGhanaNumber = phone.startsWith('+233') || phone.startsWith('233')

    if (!isGhanaNumber) {
      console.warn(`Phone ${phone} is not a Ghana number. mNotify primarily supports Ghana numbers.`)
      // For international numbers: MNotify may support them, or implement alternative provider if needed
      console.log(`[DEV] 2FA Code for ${phone}: ${otpCode}`)
      return { success: true }
    }

    // Normalize phone number for Ghana
    let normalizedPhone = phone
    if (phone.startsWith('0')) {
      normalizedPhone = '+233' + phone.substring(1)
    } else if (phone.startsWith('233')) {
      normalizedPhone = '+' + phone
    }

    const message = `Your FaithYoke 2FA code is: ${otpCode}. Valid for 5 minutes.`

    // Send SMS via mNotify
    const response = await fetch(
      `https://api.mnotify.com/api/sms/quick?key=${MNOTIFY_API_KEY}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          recipient: [normalizedPhone],
          sender: MNOTIFY_SENDER_ID,
          message: message,
          is_schedule: false,
          schedule_date: ''
        })
      }
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error('mNotify API error:', errorText)
      // Still return success but log for dev
      console.log(`[DEV] 2FA Code for ${phone}: ${otpCode}`)
      return { success: true }
    }

    const result = await response.json()
    console.log('mNotify SMS sent successfully:', result)

    // Also log for development
    console.log(`[DEV] 2FA Code sent to ${phone}: ${otpCode}`)

    return { success: true }
  } catch (error: any) {
    console.error('2FA SMS send error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Verify 2FA OTP code
 */
export async function verify2FAOTP(
  userId: string,
  code: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get unexpired codes for user
    const { data: codes, error } = await supabase
      .from('two_factor_codes')
      .select('*')
      .eq('user_id', userId)
      .eq('used', false)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(5)

    if (error || !codes || codes.length === 0) {
      return { success: false, error: 'Invalid or expired code' }
    }

    // Check code against all recent codes (in case of resends)
    const codeHash = await hashString(code)
    for (const storedCode of codes) {
      const isValid = codeHash === storedCode.code

      if (isValid) {
        // Mark code as used
        await supabase
          .from('two_factor_codes')
          .update({ used: true, used_at: new Date().toISOString() })
          .eq('id', storedCode.id)

        // Log successful 2FA
        await supabase.from('audit_logs').insert({
          user_id: userId,
          action: '2fa_verified',
          status: 'success',
          details: { method: storedCode.method }
        })

        return { success: true }
      }
    }

    // Log failed 2FA attempt
    await supabase.from('audit_logs').insert({
      user_id: userId,
      action: '2fa_verification_failed',
      status: 'failure',
      details: { reason: 'invalid_code' }
    })

    return { success: false, error: 'Invalid code' }
  } catch (error: any) {
    console.error('2FA verification error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Generate TOTP secret for authenticator app
 * Note: Not available in browser - use Supabase Auth MFA or implement via backend API
 */
export function generateTOTPSecret(userEmail: string): {
  secret: string
  otpauthUrl: string
} {
  console.warn('TOTP generation not available in browser. Use Supabase Auth MFA instead.')
  return {
    secret: '',
    otpauthUrl: ''
  }
}

/**
 * Generate QR code for TOTP setup
 * Note: Not available in browser - use Supabase Auth MFA or implement via backend API
 */
export async function generateTOTPQRCode(otpauthUrl: string): Promise<string> {
  console.warn('QR code generation not available in browser. Use Supabase Auth MFA instead.')
  return ''
}

/**
 * Verify TOTP code from authenticator app
 * Note: Not available in browser - use Supabase Auth MFA or implement via backend API
 */
export function verifyTOTPCode(secret: string, code: string): boolean {
  console.warn('TOTP verification not available in browser. Use Supabase Auth MFA instead.')
  return false
}

/**
 * Enable 2FA for a user
 */
export async function enable2FA(
  userId: string,
  method: 'sms' | 'authenticator',
  secret?: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const updates: any = {
      two_factor_enabled: true,
      two_factor_method: method
    }

    if (method === 'authenticator' && secret) {
      updates.two_factor_secret = secret
    }

    const { error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', userId)

    if (error) {
      throw error
    }

    // Log 2FA enablement
    await supabase.from('audit_logs').insert({
      user_id: userId,
      action: '2fa_enabled',
      status: 'success',
      details: { method }
    })

    return { success: true }
  } catch (error: any) {
    console.error('2FA enable error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Disable 2FA for a user
 */
export async function disable2FA(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('profiles')
      .update({
        two_factor_enabled: false,
        two_factor_method: null,
        two_factor_secret: null
      })
      .eq('id', userId)

    if (error) {
      throw error
    }

    // Log 2FA disablement
    await supabase.from('audit_logs').insert({
      user_id: userId,
      action: '2fa_disabled',
      status: 'success'
    })

    return { success: true }
  } catch (error: any) {
    console.error('2FA disable error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Generate backup codes for 2FA
 */
export function generateBackupCodes(count: number = 8): string[] {
  const codes: string[] = []
  for (let i = 0; i < count; i++) {
    // Generate 8-character alphanumeric codes
    const code = Math.random().toString(36).substring(2, 10).toUpperCase()
    codes.push(code)
  }
  return codes
}

/**
 * Store backup codes for user
 */
export async function storeBackupCodes(
  userId: string,
  codes: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Hash each backup code before storing
    const hashedCodes = await Promise.all(
      codes.map(async (code) => ({
        user_id: userId,
        code: await hashString(code),
        used: false
      }))
    )

    const { error } = await supabase.from('backup_codes').insert(hashedCodes)

    if (error) {
      throw error
    }

    return { success: true }
  } catch (error: any) {
    console.error('Backup codes storage error:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Verify backup code
 */
export async function verifyBackupCode(
  userId: string,
  code: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get unused backup codes
    const { data: backupCodes, error } = await supabase
      .from('backup_codes')
      .select('*')
      .eq('user_id', userId)
      .eq('used', false)

    if (error || !backupCodes || backupCodes.length === 0) {
      return { success: false, error: 'No valid backup codes available' }
    }

    // Check code against all backup codes
    const codeHash = await hashString(code)
    for (const storedCode of backupCodes) {
      const isValid = codeHash === storedCode.code

      if (isValid) {
        // Mark code as used
        await supabase
          .from('backup_codes')
          .update({ used: true, used_at: new Date().toISOString() })
          .eq('id', storedCode.id)

        // Log successful backup code use
        await supabase.from('audit_logs').insert({
          user_id: userId,
          action: 'backup_code_used',
          status: 'success'
        })

        return { success: true }
      }
    }

    return { success: false, error: 'Invalid backup code' }
  } catch (error: any) {
    console.error('Backup code verification error:', error)
    return { success: false, error: error.message }
  }
}
